import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Server, KeyRound, EyeOff, Trash2, FileDown, Ban, Mail, ArrowRight } from "lucide-react";

const PROMISES = [
  {
    icon: <EyeOff size={20} strokeWidth={1.5} />,
    title: "Private by default",
    body: "Only you can read what you save. Not us. Not other members. Never sold to anyone.",
  },
  {
    icon: <Lock size={20} strokeWidth={1.5} />,
    title: "Encrypted in transit",
    body: "Every page, every save and every login is served over HTTPS (TLS 1.3). Nothing is sent in the clear.",
  },
  {
    icon: <Server size={20} strokeWidth={1.5} />,
    title: "Encrypted at rest",
    body: "Your database is protected by industry-standard encryption on managed infrastructure — the same protection banks and hospitals use.",
  },
  {
    icon: <KeyRound size={20} strokeWidth={1.5} />,
    title: "Passwords you own",
    body: "Passwords are one-way hashed with bcrypt. Even I can't see them. If you forget yours, you'll reset it — you'll never be told it.",
  },
  {
    icon: <Ban size={20} strokeWidth={1.5} />,
    title: "No trackers, no ads",
    body: "No Google Analytics, no Facebook pixel, no third-party ad networks. The only external service we use is Stripe — for payment, nothing else.",
  },
  {
    icon: <ShieldCheck size={20} strokeWidth={1.5} />,
    title: "Not used to train AI",
    body: "Your Plan, your letters, your chats — none of it is ever fed to AI training pipelines. The chatbot answers your questions and forgets. Your writing stays yours.",
  },
  {
    icon: <FileDown size={20} strokeWidth={1.5} />,
    title: "You own your data",
    body: "Export your Plan as a watermarked PDF whenever you want. Delete your entire account and every trace of your data with one click, permanently.",
  },
  {
    icon: <Trash2 size={20} strokeWidth={1.5} />,
    title: "What we don't store",
    body: "No card numbers (Stripe handles payment). No location data. No device fingerprint. No marketing lists. If you're not paying for a lifetime, you'll never receive a marketing email.",
  },
];

export default function Security() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="security-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">Trust &amp; security</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D332D] leading-[1.05] tracking-tight mb-6">
        How your information is kept safe.
      </h1>
      <p className="text-lg text-[#576056] leading-relaxed mb-4">
        What you write here is often intimate — funeral wishes, family notes, the private things you never quite said. You deserve to know exactly how it's held. So here it is, in plain English.
      </p>
      <p className="text-[#576056] leading-relaxed mb-12">
        No jargon, no small print, no hidden clauses. If any of this ever stops being true, this page will change first.
      </p>

      <div className="space-y-6">
        {PROMISES.map((p, i) => (
          <div key={i} className="flex items-start gap-4 md:gap-5">
            <div className="w-11 h-11 rounded-full bg-[#E9ECE8] border border-[#9EAD94]/40 flex items-center justify-center text-[#6B7F62] flex-shrink-0 mt-0.5">
              {p.icon}
            </div>
            <div>
              <div className="font-serif text-xl text-[#2D332D] leading-snug mb-1.5">{p.title}</div>
              <p className="text-[#576056] leading-relaxed">{p.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 rounded-2xl bg-[#F5ECE7] border border-[#D1A793]/40 p-6 md:p-8">
        <div className="font-serif text-2xl text-[#2D332D] mb-2">Your rights under UK GDPR</div>
        <p className="text-[#576056] leading-relaxed mb-4">
          You can ask for a copy of everything we hold, ask us to correct it, ask us to delete it, or ask us to send it to another service — at any time, for any reason, without giving one. You have the right to complain to the ICO if you feel we've mishandled anything.
        </p>
        <p className="text-[#576056] leading-relaxed mb-4">
          All Day VA is registered with the Information Commissioner's Office (ICO), registration number <span className="font-mono text-[#2D332D]">ZC042995</span>.
        </p>
        <p className="text-[#576056] leading-relaxed">
          Email <a href="mailto:lauren@alldayva.co.uk" className="text-[#6B7F62] underline hover:text-[#2D332D]">lauren@alldayva.co.uk</a> and I'll reply within 7 days.
        </p>
      </div>

      <div className="mt-8 rounded-2xl border border-[#EAE6E1] p-6 md:p-8">
        <div className="font-serif text-2xl text-[#2D332D] mb-2">Something feel off? Tell me.</div>
        <p className="text-[#576056] leading-relaxed mb-4">
          If you spot something that doesn't feel right, or if you have a security concern — please write. I read every message myself.
        </p>
        <a
          href="mailto:lauren@alldayva.co.uk"
          className="inline-flex items-center gap-2 text-[#6B7F62] font-medium hover:text-[#2D332D]"
          data-testid="security-contact-link"
        >
          <Mail size={17} /> lauren@alldayva.co.uk
        </a>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3 text-sm text-[#8A9488]">
        <Link to="/privacy" className="inline-flex items-center gap-1.5 hover:text-[#2D332D]" data-testid="security-to-privacy">
          Read the short privacy notice <ArrowRight size={14} />
        </Link>
        <span>·</span>
        <Link to="/register" className="hover:text-[#2D332D]">Start your Plan</Link>
      </div>
    </div>
  );
}
